import { createSlice, PayloadAction } from '@reduxjs/toolkit'

import {
  FiscalCodeLocation,
  FiscalCodeLocationDraft,
  FiscalCodeLocationPayload,
  FiscalCodeTable,
  ItalyFiscalCodesManagementState,
  LoadSuccessPayload,
  SaveFailurePayload,
} from './types'

const emptyDraft: FiscalCodeLocationDraft = { id: null, code: '', name: '', originalCode: '', originalName: '' }

export const initialState: ItalyFiscalCodesManagementState = {
  table: 'cities',
  searchInput: '',
  search: '',
  page: 1,
  locations: [],
  total: 0,
  lastPage: 1,
  isLoading: false,
  loadError: '',
  isModalOpen: false,
  draft: emptyDraft,
  fieldErrors: {},
  formError: '',
  isSaving: false,
  deleteTarget: null,
  isDeleting: false,
}

const selectTableHandler = (state: ItalyFiscalCodesManagementState, action: PayloadAction<FiscalCodeTable>) => {
  state.table = action.payload
  state.searchInput = ''
  state.search = ''
  state.page = 1
}

const setSearchInputHandler = (state: ItalyFiscalCodesManagementState, action: PayloadAction<string>) => {
  state.searchInput = action.payload
}

const applySearchHandler = (state: ItalyFiscalCodesManagementState, action: PayloadAction<string>) => {
  state.search = action.payload
  state.page = 1
}

const setPageHandler = (state: ItalyFiscalCodesManagementState, action: PayloadAction<number>) => {
  state.page = action.payload
}

const loadRequestHandler = (state: ItalyFiscalCodesManagementState) => {
  state.isLoading = true
  state.loadError = ''
}

const loadSuccessHandler = (state: ItalyFiscalCodesManagementState, action: PayloadAction<LoadSuccessPayload>) => {
  state.isLoading = false
  state.locations = action.payload.locations
  state.total = action.payload.total
  state.lastPage = Math.max(action.payload.lastPage, 1)
  if (state.page > state.lastPage) state.page = state.lastPage
}

const loadFailureHandler = (state: ItalyFiscalCodesManagementState, action: PayloadAction<string>) => {
  state.isLoading = false
  state.loadError = action.payload
  state.locations = []
}

const openCreateModalHandler = (state: ItalyFiscalCodesManagementState) => {
  state.isModalOpen = true
  state.draft = emptyDraft
  state.fieldErrors = {}
  state.formError = ''
}

const openEditModalHandler = (state: ItalyFiscalCodesManagementState, action: PayloadAction<FiscalCodeLocation>) => {
  const { id, code, name } = action.payload
  state.isModalOpen = true
  state.draft = { id, code, name, originalCode: code, originalName: name }
  state.fieldErrors = {}
  state.formError = ''
}

const closeModalHandler = (state: ItalyFiscalCodesManagementState) => {
  state.isModalOpen = false
  state.isSaving = false
  state.draft = emptyDraft
  state.fieldErrors = {}
  state.formError = ''
}

const updateDraftHandler = (
  state: ItalyFiscalCodesManagementState,
  action: PayloadAction<Partial<FiscalCodeLocationPayload>>,
) => {
  state.draft = { ...state.draft, ...action.payload }
  Object.keys(action.payload).forEach(field => {
    delete state.fieldErrors[field as keyof FiscalCodeLocationPayload]
  })
  state.formError = ''
}

const saveRequestHandler = (state: ItalyFiscalCodesManagementState) => {
  state.isSaving = true
  state.fieldErrors = {}
  state.formError = ''
}

const saveFailureHandler = (state: ItalyFiscalCodesManagementState, action: PayloadAction<SaveFailurePayload>) => {
  const { field, message } = action.payload
  state.isSaving = false
  if (field) {
    state.fieldErrors[field] = message
    return
  }
  state.formError = message
}

const askDeleteHandler = (state: ItalyFiscalCodesManagementState, action: PayloadAction<FiscalCodeLocation>) => {
  state.deleteTarget = action.payload
}

const deleteRequestHandler = (state: ItalyFiscalCodesManagementState) => {
  state.isDeleting = true
}

const closeDeleteModalHandler = (state: ItalyFiscalCodesManagementState) => {
  state.deleteTarget = null
  state.isDeleting = false
}

const clearHandler = () => initialState

export const italyFiscalCodesManagementSlice = createSlice({
  name: 'italyFiscalCodesManagement',
  initialState,
  reducers: {
    selectTable: selectTableHandler,
    setSearchInput: setSearchInputHandler,
    applySearch: applySearchHandler,
    setPage: setPageHandler,
    loadRequest: loadRequestHandler,
    loadSuccess: loadSuccessHandler,
    loadFailure: loadFailureHandler,
    openCreateModal: openCreateModalHandler,
    openEditModal: openEditModalHandler,
    closeModal: closeModalHandler,
    updateDraft: updateDraftHandler,
    saveRequest: saveRequestHandler,
    saveFailure: saveFailureHandler,
    askDelete: askDeleteHandler,
    deleteRequest: deleteRequestHandler,
    closeDeleteModal: closeDeleteModalHandler,
    clearItalyFiscalCodesManagement: clearHandler,
  },
})

export const {
  selectTable,
  setSearchInput,
  applySearch,
  setPage,
  loadRequest,
  loadSuccess,
  loadFailure,
  openCreateModal,
  openEditModal,
  closeModal,
  updateDraft,
  saveRequest,
  saveFailure,
  askDelete,
  deleteRequest,
  closeDeleteModal,
  clearItalyFiscalCodesManagement,
} = italyFiscalCodesManagementSlice.actions

export default italyFiscalCodesManagementSlice.reducer
